<template>
  <v-container>
    <v-row justify="center">
      <v-col class="mb-5 mt-5" cols="12" md="9" lg="7" xl="5">
        <v-card elevation="5">

          <!-- Header - Club name, form description, club logo -->
          <v-list-item three-line>
            <v-list-item-content>

              <div class="text-overline">
                {{ clubData.Vereins_Name }}
              </div>

              <v-list-item-title class="text-h5 mb-1">
                {{ $t("registration.registrationForm") }}
              </v-list-item-title>

              <v-list-item-subtitle>
                {{ $t("registration.titleNote", { club: clubData.Vereins_Name }) }}
              </v-list-item-subtitle>

            </v-list-item-content>

            <v-list-item-avatar tile size="150">
              <v-img :src="'data:image/png;base64, ' + clubData.Logo" contain />
            </v-list-item-avatar>
          </v-list-item>

          <!-- Registration form -->
          <v-list-item>
            <v-container>
              <v-form ref="form" id="registration-form" @submit.prevent="onSubmit">
                <!-- Information while printing pdf -->
                <v-row v-if="isGeneratingPdf">
                  <v-col cols="auto" class="pt-0 pb-0">
                    <h4>{{ headerText }}</h4>
                  </v-col>
                </v-row>
                <v-row v-if="isGeneratingPdf">
                  <v-divider />
                </v-row>

                <!-- Salutation -->
                <v-row>
                  <v-col cols="12" class="pt-0 pb-0">
                    <v-autocomplete v-model="item.salutation" :label="$t('registration.salutation')" :items="salutation"
                      :item-value="'key'" :item-text="'value'" auto-select-first hide name="salutation" />
                  </v-col>
                </v-row>

                <!-- Name input (consists of title, firstname, lastname) -->
                <v-row>
                  <v-col cols="12" sm="2" class="pt-0">
                    <v-text-field v-model="item.title" ref="titleText" :label="$t('registration.title')" :maxlength="40"
                      counter />
                  </v-col>

                  <v-col cols="6" sm="5" class="pt-0 ">
                    <v-text-field v-model="item.firstName" ref="firstNameText" :label="$t('registration.firstName')"
                      :maxlength="40" counter :rules="rules.required" />
                  </v-col>
                  <v-col cols="6" sm="5" class="pt-0">
                    <v-text-field v-model="item.lastName" ref="lastNameText" :label="$t('registration.lastName')"
                      :maxlength="40" counter :rules="rules.required" />
                  </v-col>

                </v-row>

                <!-- Gender and birthdate -->
                <v-row>
                  <v-col class="pb-0">
                    <v-autocomplete v-model="item.gender" :label="$t('registration.gender')" :items="gender"
                      :item-value="'key'" :item-text="'value'" auto-select-first hide name="gender" />
                  </v-col>
                  <v-col class="pb-0">
                    <date-time-picker v-model="item.birthDay" text-field-clearable is-date-only
                      :text-field-label="$t('registration.birthDate')" :text-field-rules="rules.birthDate" />
                  </v-col>
                </v-row>

                <!-- Street -->
                <v-row>
                  <v-col class="pt-0">
                    <v-text-field v-model="item.street" :label="$t('registration.street')" :maxlength="40" counter
                      :rules="rules.required" />
                  </v-col>
                </v-row>

                <!-- Zipcode, city and countrycode  -->
                <v-row>
                  <v-col cols="3" class="pt-0 pb-0">
                    <v-text-field name="zip" v-model="item.postCode" :label="$t('registration.zipCode')"
                      :rules="rules.required" @keypress="isNumber($event)" :maxlength="5" counter />
                  </v-col>
                  <v-col cols="7" class="pt-0 pb-0">
                    <!-- Information about maxlength, see: https://cvpnet.visualstudio.com/CVPnet%20Organisation%20und%20Planung/_workitems/edit/1413#5392820 -->
                    <v-text-field v-model="item.city" :label="$t('registration.city')" :maxlength="32" counter
                      :rules="rules.required" />
                  </v-col>
                  <v-col cols="2" class="pt-0 pb-0">
                    <v-text-field v-model="item.countryCode" :label="$t('registration.countryCode')"
                      :rules="rules.required" :maxlength="3" counter />
                  </v-col>
                </v-row>

                <!-- Phone numbers -->
                <v-row>
                  <v-col class="pt-0 pb-0">
                    <v-text-field name="mobile-phone" v-model="item.mobile" :label="$t('registration.mobile')"
                      @keydown.shift="forbiddenChar" @keydown.alt="forbiddenChar" @keydown.exact="checkInputChar"
                      @paste="checkPastedValue" :maxlength="25" counter />
                  </v-col>
                  <v-col class="pt-0 pb-0">
                    <v-text-field name="phone" v-model="item.telephone" :label="$t('registration.phone')"
                      @keydown.shift="forbiddenChar" @keydown.alt="forbiddenChar" @keydown.exact="checkInputChar"
                      @paste="checkPastedValue" :maxlength="25" counter />
                  </v-col>
                </v-row>

                <v-row>
                  <v-col class="pt-0 pb-0">
                    <v-text-field v-model="item.eMail" name="email" :label="$t('registration.eMail')"
                      :rules="rules.required" :maxlength="60" counter />
                  </v-col>
                </v-row>

                <v-row>
                  <v-spacer />
                  <v-col class="pt-0 pb-0 imageInput" cols="auto" v-if="file != null">
                    <v-img max-height="100" max-width="100" :src="previewUrl" />
                  </v-col>
                  <v-spacer />
                </v-row>

                <v-row>
                  <v-col class="pt-0 pb-0 imageInput">

                    <v-file-input :disabled="isLoading" :label="$t('registration.uploadPhoto')" v-model="file" show-size
                      prepend-icon="" append-icon="$file" truncate-length="50" @change="previewImage"
                      :rules="rules.profileImage" accept="image/png, image/jpeg, image/bmp" />
                  </v-col>
                </v-row>

                <v-row>
                  <v-col class="pt-0 pb-0">
                    <v-text-field v-model="item.iban" @input="item.iban = item.iban.toUpperCase()"
                      v-mask="'AA## XXXX XXXX XXXX XXXX XXXX XXXX XXXX XX'" placeholder="DE## #### #### #### #### ##"
                      name="iban" :label="$t('registration.iban')" :rules="rules.iban" />
                  </v-col>
                </v-row>

                <v-row>
                  <v-col class="pb-0">
                    <v-checkbox v-model="differentAccountOwner" :label="$t('registration.accountOwner')"
                      style="padding: 0; margin: 0" :rules="rules.minor" />
                  </v-col>
                </v-row>

                <v-row v-if="differentAccountOwner">
                  <v-col cols="6" sm="4" class="pt-0 pb-0">
                    <v-text-field v-model="item.accOwnerFirstName" :label="$t('registration.firstName')"
                      :rules="rules.required" :maxlength="40" counter />
                  </v-col>
                  <v-col cols="6" sm="4" class="pt-0 pb-0">
                    <v-text-field v-model="item.accOwnerLastName" :maxlength="40" counter
                      :label="$t('registration.lastName')" :rules="rules.required" />
                  </v-col>
                  <v-col cols="6" sm="4" class="pt-0 pb-0">
                    <v-text-field v-model="item.accOwnerEMail" name="email" :maxlength="60" counter
                      :label="$t('registration.eMail')" :rules="rules.required" />
                  </v-col>
                </v-row>

                <v-row>
                  <v-col>
                    <v-textarea v-model="item.infoText" rows="2" dense :label="$t('registration.clubInformation')"
                      :placeholder="$t('registration.clubInformationPlaceholder')" :rules="rules.required"
                      hide-details />
                  </v-col>
                </v-row>

                <!-- Additional fields -->
                <v-row v-if="additionalFieldsInternal && additionalFieldsInternal.length > 0" class="pt-2">
                  <v-col v-for="(field, index) in additionalFieldsInternal" :key="index" cols="12" class="pt-0 pb-0">
                    <!-- Type = "Text" -->
                    <v-text-field v-if="field.Type.toLowerCase() === 'text'" v-model="field.Value"
                      :rules="field.Required ? rules.required : []" :label="field.Text" />
                    <!-- Type = "Checkbox" -->
                    <v-checkbox v-else-if="field.Type.toLowerCase() === 'checkbox'" v-model="field.Value"
                      :rules="field.Required ? [(val) => val || $t('error.requiredCheckboxGeneric')] : []" :label="field.Text" />
                  </v-col>
                </v-row>

                <v-row>
                  <v-col class="pt-0 pb-0">
                    <v-checkbox v-model="item.payDebitCharge" :label="$t('registration.payDebitCharge')"
                      :rules="[(val) => val || this.$t('error.requiredConsent')]" />
                  </v-col>
                </v-row>

                <v-row v-if="privacyPolicyUrl.length > 0">
                  <v-col class="pt-0 pb-0">
                    <v-checkbox v-model="acceptPrivacyPolicy"
                      :rules="[(val) => val || this.$t('error.requiredPrivacyPolicy')]">
                      <div @click.stop slot="label">
                        {{ $tc("registration.acceptPrivacyPolicy", true) }} <a :href="'//' + privacyPolicyUrl"
                          target="_blank">{{ $t("registration.privacyPolicy") }}</a> {{
                            $tc("registration.acceptPrivacyPolicy", false) }}
                      </div>
                    </v-checkbox>
                  </v-col>
                </v-row>

                <v-row v-show="isMinor">
                  <v-col class="pt-0 pb-0">
                    <v-checkbox v-model="acceptMinorPolicy" :label="$t('registration.minorPolicy')"
                      :rules="rules.minor" />
                  </v-col>
                </v-row>

                <v-row>
                  <v-col class="py-0 px-5">
                    <VueHcaptcha ref="recaptcha" @verify="onCaptchaVerify" @expired="onCaptchaExpired"
                      @closed="onCaptchaClose" @error="onCaptchaError" size="invisible"
                      sitekey="02f5f991-6331-4f82-8967-66599a1bb15a" />
                  </v-col>
                </v-row>
              </v-form>
            </v-container>
          </v-list-item>

          <p class="red--text text-center" v-if="captchaClosed">{{ $t("registration.hCaptchaClosedMessage") }}</p>
          <p class="red--text text-center" v-if="captchaError">{{ $t("registration.hCaptchaErrorMessage") }}</p>

          <v-card-actions>
            <v-btn outlined text type="submit" form="registration-form" width="100%" :loading="isLoading"
              :disabled="disableSubmit">{{ $t("registration.register") }}</v-btn>
          </v-card-actions>
        </v-card>
      </v-col>
    </v-row>

    <v-alert :type="alert.type" elevation="10" :dismissible="alert.dismissable" :value="alert.isActive">
      {{ alert.text }}
    </v-alert>
  </v-container>
</template>

<script>
import VueHcaptcha from "@hcaptcha/vue-hcaptcha";
import DateTimePicker from "./shared/DateTimePicker.vue";
import { checkInputForNumber, valueIsDecimal } from "@/scripts/global";
import { mapGetters } from "vuex";
import html2pdf from "html2pdf.js";

export default {
  components: {
    DateTimePicker,
    VueHcaptcha,
  },

  props: {
    alert: { type: Object, require: true },
    privacyPolicyUrl: { type: String, default: "" },
    additionalFields: { type: Array, require: true, default: () => [] },
  },

  data: () => ({
    item: {
      salutation: 1,
      gender: 1,
      birthDay: new Date().toISOString(),
      countryCode: "DE",
      payDebitCharge: false,
      iban: "",
      postCode: undefined,
    },
    additionalFieldsInternal: [],
    nameCharacters: 0,
    previewUrl: null,
    file: null,
    isLoading: true,
    acceptPrivacyPolicy: false,
    acceptMinorPolicy: false,
    accountOwner: {},
    differentAccountOwner: false,
    isGeneratingPdf: false,
    captchaError: false,
    captchaClosed: false,
  }),

  computed: {
    ...mapGetters({ clubData: "GET_CLUB_DATA" }),
    rules() {
      return {
        required: [(val) => (val || "").length > 0 || this.$t("error.required")],
        birthDate: [(val) => val != null || this.$t("error.invalidDate")],
        iban: [(val) => !this.item.payDebitCharge || (val || "").length > 0 || this.$t("error.required")],
        minor: [(val) => val || (!val && !this.isMinor) || this.$t("error.requiredMinor")],
        profileImage: [
          (val) => {
            if (!val) return true;
            const isImage = val.type.startsWith("image/");
            const isValidSize = val.size < 5242880;

            if (!isImage) {
              return this.$t("error.invalidImageType");
            }

            return isValidSize || this.$t("error.imageTooLarge");
          }
        ],
      };
    },
    gender() {
      return [
        { key: 1, value: this.$t("registration.male") },
        { key: 2, value: this.$t("registration.female") },
        { key: 3, value: this.$t("registration.diverse") },
        { key: 4, value: this.$t("registration.notSpecified") },
      ];
    },
    salutation() {
      return [
        { key: 1, value: this.$t("registration.mr") },
        { key: 2, value: this.$t("registration.mrs") },
        { key: 5, value: "- - -" },
        { key: 4, value: this.$t("registration.family") },
      ];
    },
    isMinor() {
      const bd = new Date(this.item.birthDay);
      const result = bd.setFullYear(bd.getFullYear() + 16) > new Date();
      return result;
    },
    disableSubmit() {
      return (this.privacyPolicyUrl.length > 0 && !this.acceptPrivacyPolicy) || (this.item.iban.length > 0 && !this.item.payDebitCharge);
    },
    headerText() {
      return "Die folgende Person hat sich am " + new Date().toLocaleDateString() + " (" + new Date().toLocaleTimeString() + ") in Ihrem Verein als neues Mitglied angemeldet und folgende Daten übermittelt:";
    },
  },

  watch: {
    clubData(v) {
      this.isLoading = !(v != null && Object.entries(v).length > 0);
    },
    differentAccountOwner(v) {
      if (!v) {
        this.item.accOwnerEMail = "";
        this.item.accOwnerFirstName = "";
        this.item.accOwnerLastName = "";
      }
    },
    acceptMinorPolicy() {
      this.$refs.form.validate();
    },

    additionalFields(newVal) {
      this.additionalFieldsInternal = newVal;
    },
  },

  methods: {
    isNumber: function (evt) {
      evt = (evt) ? evt : window.event;
      var charCode = (evt.which) ? evt.which : evt.keyCode;
      if (charCode < 47 || charCode > 58) {
        evt.preventDefault();
      } else {
        return true;
      }
    },
    onSubmit() {
      this.isLoading = true;
      console.log("onSubmit");
      this.captchaClosed = false;
      this.captchaError = false;
      console.log("Form: " + this.$refs.form);
      console.log("Form is valid: " + this.$refs.form.validate());

      if (this.$refs.form.validate()) {
        this.$refs.recaptcha.execute();
      } else {
        this.isLoading = false;
      }

    },

    onCaptchaClose() {
      this.captchaClosed = true;
      this.isLoading = false;
    },

    onCaptchaError() {
      this.captchaError = true;
      this.isLoading = false;
    },

    onCaptchaVerify(recaptchaToken) {
      if (this.item.iban != null && this.item.iban.length > 0) {
        this.item.iban = this.item.iban.toUpperCase();
      }

      this.isGeneratingPdf = true;
      this.$nextTick(() => {
        html2pdf()
          .from(document.getElementById("registration-form"))
          .set({
            margin: [10, 15, 10, 15],
            filename: "Anmeldung.pdf",
            image: { type: "jpeg", quality: 0.98 },
            enableLinks: true,
            html2canvas: {
              scale: 2,
              logging: false,
              ignoreElements: (element) => {
                if (element.classList.contains("v-text-field__details") || element.classList.contains("imageInput")) {
                  return true;
                }
                return false;
              },
            },
            pagebreak: { mode: "" },
            jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
          })
          .toContainer()
          .toCanvas()
          .toImg()
          .toPdf()
          .outputPdf("datauristring")
          .then((pdf) => {
            this.isGeneratingPdf = false;
            pdf = pdf.replace("data:application/pdf;base64,", "");
            if (this.file != null) {
              var reader = new FileReader();
              reader.readAsDataURL(this.file);
              reader.onload = () => {
                this.item.photo = reader.result.replace(/^data:image\/(png|jpg|jpeg);base64,/, "");
                this.$emit("submit", { token: recaptchaToken, item: this.item, additionalFields: this.additionalFieldsInternal, pdf: pdf });
              };
            } else {
              this.$emit("submit", { token: recaptchaToken, item: this.item, additionalFields: this.additionalFieldsInternal, pdf: pdf });
            }
          });
      });

      this.isLoading = false;
    },

    onCaptchaExpired() {
      this.$refs.recaptcha.reset();
      this.isLoading = false;
    },

    previewImage() {
      this.previewUrl = this.file != null ? URL.createObjectURL(this.file) : "";
    },

    checkInputChar(event) {
      if (!checkInputForNumber(event, this.withDecimalPlaces_, this.$i18n.locale)) {
        event.preventDefault();
      }
    },

    forbiddenChar(event) {
      event.preventDefault();
    },

    checkPastedValue(event) {
      const newValue = event.clipboardData.getData("text");
      if (!valueIsDecimal(newValue)) {
        event.preventDefault();
      }
    },

    checkBirthDay(date) {
      const dateParts = date.split(".");
      const birthDateSet = date != null && dateParts[2] !== new Date().getFullYear().toString();
      const minorCheck = !this.isMinor || (this.isMinor && this.acceptMinorPolicy);
      return birthDateSet && minorCheck;
    },
  },
};
</script>

<style lang="scss">
.v-alert {
  position: fixed !important;
  left: 50%;
  top: 20%;
  transform: translate(-50%, -50%);
  margin: 0 auto;
}

.v-input {
  padding-top: 0;
}
</style>
